import { Box, SxProps, Tab, Theme } from '@mui/material';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { TabsWrapper } from '../../../components/customer/reports/reportDocuments/styled-components';
import { ComponentTabsType } from '../../../components/customer/reports/reportDocuments/types/ComponentTabsType';
import FinalReport from '../../../components/customer/settings/finalReport/finalReport';
import FinalReportsListTable from '../../../components/customer/settings/finalReport/listTable';

import { Text } from '~/components/ui/text';
import { ProcessTemplates } from '~/pages/customer/settings/final-report/processes/route';
import QueryResolver from '~/services/api/query-resolver';
import { UserService } from '~/services/api/users/endpoints';

const scrollButtonStyle: SxProps<Theme> = {
  color: '#878787',
  fontSize: '13px',
  width: '36px',
  height: '36px',
  borderRadius: '117px',
  border: '1px solid #878787',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const RightScrollButton = () => <Box sx={scrollButtonStyle}>{'>'}</Box>;
const LeftScrollButton = () => <Box sx={scrollButtonStyle}>{'<'}</Box>;

const CustomerSettingsFinalReport = () => {
  const intl = useIntl();
  const flagsQuery = UserService.endpoints.flags.useQuery();
  const componentTabs: Array<ComponentTabsType> = useMemo(
    () => [
      {
        itemKey: 'reports',
        value: intl.formatMessage({ defaultMessage: 'Finalized Reports' }),
        tabKey: 0,
      },
      {
        itemKey: 'templates',
        value: intl.formatMessage({ defaultMessage: 'Final Report Templates' }),
        tabKey: 1,
      },
      {
        itemKey: 'processes',
        value: intl.formatMessage({ defaultMessage: 'Process Templates' }),
        tabKey: 2,
      },
    ],
    [intl],
  );

  const tabsDisplay = componentTabs.map(
    ({ itemKey, value, tabKey }: ComponentTabsType) => {
      return (
        <Tab
          sx={{
            borderTopLeftRadius: tabKey === 0 ? '8px' : '0px',
          }}
          value={tabKey}
          label={value}
          key={`tabKey-${itemKey}`}
          wrapped
        />
      );
    },
  );

  const [selectedTab, setSelectedTab] = useState<number>(
    componentTabs[0].tabKey,
  );

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    event.preventDefault();
    setSelectedTab(newValue);
  };

  return (
    <QueryResolver query={flagsQuery}>
      {(data) => (
        <Box height='100%'>
          <TabsWrapper
            value={selectedTab}
            sx={{
              backgroundColor: '#fff',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              borderBottomRightRadius: '0px',
              borderBottomLeftRadius: '0px',
              borderTop: '1px solid rgba(0, 0, 0, 0.16)',
              borderLeft: '1px solid rgba(0, 0, 0, 0.16)',
              borderRight: '1px solid rgba(0, 0, 0, 0.16)',
              '& .MuiTabScrollButton-root:first-child': {
                marginLeft: '8px !important',
              },
              '& .MuiTabScrollButton-root:last-child': {
                marginRight: '8px !important',
              },
              '& > .MuiTabs-scroller > .MuiTabs-flexContainer > .MuiButtonBase-root:last-child':
              {
                borderRight: '0px solid transparent',
              },
              '& > .MuiTabs-scroller > .MuiTabs-flexContainer > .MuiButtonBase-root':
              {
                borderBottom: '0px solid transparent',
              },
              '& > .MuiTabs-scroller > .MuiTabs-flexContainer > .MuiButtonBase-root.Mui-selected':
              {
                color: '#00CECF',
                backgroundColor: '#E0F9F9',
                borderBottom: '#00CECF',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#00CECF !important',
              },
              '& > .MuiTabs-scroller': {
                borderBottom: '1px solid #D6D6D6',
              },
            }}
            defaultValue={selectedTab}
            onChange={handleChangeTab}
            slots={{
              EndScrollButtonIcon: RightScrollButton,
              StartScrollButtonIcon: LeftScrollButton,
            }}
            variant='scrollable'
            scrollButtons='auto'
            allowScrollButtonsMobile
          >
            {tabsDisplay}
          </TabsWrapper>
          <Box
            display={selectedTab === componentTabs[0].tabKey ? '' : 'none'}
            height='80%'
          >
            <FinalReportsListTable />
          </Box>
          <Box
            display={selectedTab === componentTabs[1].tabKey ? '' : 'none'}
            height='80%'
          >
            {data.is_allowed_to_create_final_report_template ? (
              <FinalReport />
            ) : (
              <div className='flex items-center justify-center h-full bg-white border-l border-r border-b border-neutral-300 rounded-bl-lg rounded-br-lg'>
                <Text>{intl.formatMessage({ defaultMessage: 'You are not allowed to create final report templates. Contact your administrator to request access.' })}</Text>
              </div>
            )}
          </Box>
          <Box
            display={selectedTab === componentTabs[2].tabKey ? '' : 'none'}
            height='80%'
          >
            <ProcessTemplates />
          </Box>
        </Box>
      )}
    </QueryResolver>
  );
};

export default CustomerSettingsFinalReport;
